/*
 * The API is the authority for authorization.  This policy is intentionally
 * also usable before /me has received its capability list so that navigation
 * does not briefly expose every route during the initial render.
 */

export const CAPABILITY = Object.freeze({
  // The API currently exposes write capabilities for operational modules;
  // read-only screens use the corresponding server claim as their gate.
  DASHBOARD_READ: "reports.read",
  TENANT_SETTINGS_READ: "tenant.settings.write",
  TENANT_SETTINGS_MANAGE: "tenant.settings.write",
  TENANT_ROLES_MANAGE: "roles.assign",
  TENANT_EXPORT: "tenant.export",
  AUDIT_READ: "audit.read",
  ADMISSIONS_MANAGE: "admissions.write",
  STUDENTS_READ: "students.read",
  STUDENTS_MANAGE: "students.write",
  ACADEMIC_READ: "academics.write",
  ACADEMIC_MANAGE: "academics.write",
  ACADEMIC_ASSESS: "academics.write",
  ATTENDANCE_MANAGE: "attendance.write",
  FINANCE_READ: "finance.read",
  FINANCE_MANAGE: "finance.write",
  COMMUNICATION_MANAGE: "communication.write",
  TRANSPORT_READ: "transport.write",
  TRANSPORT_MANAGE: "transport.write",
  DOCUMENTS_READ: "documents.read",
  DOCUMENTS_MANAGE: "documents.write",
  STAFF_MANAGE: "staff.manage",
  SAFETY_READ: "safety.read",
  SAFETY_MANAGE: "safety.write",
  KIOSK_MANAGE: "kiosk.use",
  REPORTS_READ: "reports.read",
  REPORTS_EXPORT: "reports.export",
  PLATFORM_CONFIG_READ: "platform.config.read",
  PLATFORM_CONFIG_WRITE: "platform.config.write",
  FILES_READ: "documents.read",
  FILES_MANAGE: "documents.write",
  NURSERY_MANAGE: "academics.write",
  RESOURCES_READ: "gl.read",
  GL_WRITE: "gl.write",
  AI_USE: "ai.use",
  INVENTORY_ISSUE: "inventory.issue",
  INVENTORY_MANAGE: "inventory.manage",
  INVENTORY_REPORT: "inventory.report",
});

const operational = [
  CAPABILITY.DASHBOARD_READ,
  CAPABILITY.ADMISSIONS_MANAGE,
  CAPABILITY.STUDENTS_READ,
  CAPABILITY.STUDENTS_MANAGE,
  CAPABILITY.ACADEMIC_READ,
  CAPABILITY.ACADEMIC_MANAGE,
  CAPABILITY.ATTENDANCE_MANAGE,
  CAPABILITY.FINANCE_READ,
  CAPABILITY.FINANCE_MANAGE,
  CAPABILITY.COMMUNICATION_MANAGE,
  CAPABILITY.TRANSPORT_READ,
  CAPABILITY.TRANSPORT_MANAGE,
  CAPABILITY.DOCUMENTS_READ,
  CAPABILITY.DOCUMENTS_MANAGE,
  CAPABILITY.STAFF_MANAGE,
  CAPABILITY.SAFETY_READ,
  CAPABILITY.SAFETY_MANAGE,
  CAPABILITY.KIOSK_MANAGE,
  CAPABILITY.REPORTS_READ,
  CAPABILITY.FILES_READ,
  CAPABILITY.FILES_MANAGE,
  CAPABILITY.NURSERY_MANAGE,
  CAPABILITY.INVENTORY_ISSUE,
];

// This is only a conservative rendering fallback.  Every write remains
// protected by the API, and a capability list returned by /me replaces it.
const tenantManagement = [
    ...operational,
    CAPABILITY.RESOURCES_READ,
    CAPABILITY.GL_WRITE,
    CAPABILITY.INVENTORY_MANAGE,
    CAPABILITY.INVENTORY_REPORT,
    CAPABILITY.TENANT_SETTINGS_READ,
    CAPABILITY.TENANT_SETTINGS_MANAGE,
    CAPABILITY.TENANT_ROLES_MANAGE,
    CAPABILITY.TENANT_EXPORT,
    CAPABILITY.AUDIT_READ,
    CAPABILITY.ACADEMIC_ASSESS,
    CAPABILITY.REPORTS_EXPORT,
];

export const ROLE_CAPABILITIES = Object.freeze({
  platform_admin: Object.values(CAPABILITY),
  tenant_owner: tenantManagement,
  administrator: tenantManagement,
  principal: tenantManagement,
  accountant: [
    CAPABILITY.DASHBOARD_READ,
    CAPABILITY.STUDENTS_READ,
    CAPABILITY.FINANCE_READ,
    CAPABILITY.FINANCE_MANAGE,
    CAPABILITY.RESOURCES_READ,
    CAPABILITY.REPORTS_READ,
    CAPABILITY.REPORTS_EXPORT,
    CAPABILITY.FILES_READ,
    CAPABILITY.AI_USE,
  ],
  teacher: [
    CAPABILITY.DASHBOARD_READ,
    CAPABILITY.STUDENTS_READ,
    CAPABILITY.ACADEMIC_READ,
    CAPABILITY.ACADEMIC_ASSESS,
    CAPABILITY.ATTENDANCE_MANAGE,
    CAPABILITY.COMMUNICATION_MANAGE,
    CAPABILITY.NURSERY_MANAGE,
    CAPABILITY.INVENTORY_ISSUE,
    CAPABILITY.FILES_READ,
    CAPABILITY.AI_USE,
  ],
  guardian: [
    CAPABILITY.STUDENTS_READ,
    CAPABILITY.FINANCE_READ,
    CAPABILITY.COMMUNICATION_MANAGE,
    CAPABILITY.ACADEMIC_READ,
    CAPABILITY.TRANSPORT_READ,
    CAPABILITY.FILES_READ,
    CAPABILITY.AI_USE,
  ],
});

const capabilityValues = (value) => {
  if (Array.isArray(value)) return value.filter((entry) => typeof entry === "string");
  if (value && typeof value === "object") {
    return Object.entries(value)
      .filter(([, enabled]) => enabled === true)
      .map(([name]) => name);
  }
  return null;
};

/** Return server claims, or null when talking to an older /me endpoint. */
export const serverCapabilities = (user) => {
  for (const candidate of [
    user?.capabilities,
    user?.effective_capabilities,
    user?.permissions?.capabilities,
    user?.claims?.capabilities,
  ]) {
    const values = capabilityValues(candidate);
    if (values) return new Set(values);
  }
  return null;
};

export const capabilitiesForUser = (user) => {
  const server = serverCapabilities(user);
  if (server) return server;
  return new Set((user?.roles || []).flatMap((role) => ROLE_CAPABILITIES[role] || []));
};

export const hasCapability = (user, required) => {
  const requirements = Array.isArray(required) ? required : [required];
  return requirements.some((capability) => capabilitiesForUser(user).has(capability));
};

export const navigationForUser = (user, items) =>
  items.filter((item) => !item.capability || hasCapability(user, item.capability));

export const roleCapabilities = (role) => new Set(ROLE_CAPABILITIES[role] || []);
